import { Chip, ChipProps } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { readableColor } from 'polished';

/** Slightly darkens raw pastel hex/CSS colors for better contrast on the chip. */
const DARKEN_COEFFICIENT = 0.12;

export type CustomChipProps = Omit<ChipProps, 'color'> & {
  /** Accent color (hex or CSS color). Outlined: border; filled: border and background. */
  chipColor: string;
  /** When false, does not darken the color (e.g. for timeline gradient stops). */
  useDarken?: boolean;
  /**
   * Filled variant only: label/icon color. Use when `readableColor` (luminance > 0.179 → black)
   * disagrees with design — e.g. white on saturated orange `#ed6c02`.
   */
  filledLabelColor?: string;
};

/**
 * MUI Chip with an arbitrary accent color (not a theme palette `color` key).
 * Colors are darkened slightly for readability. Outlined: border and label match; filled: fill + border.
 */
export function CustomChip({
  chipColor,
  variant = 'outlined',
  sx,
  useDarken = true,
  filledLabelColor,
  ...rest
}: CustomChipProps) {
  const accent = useDarken ? darken(chipColor, DARKEN_COEFFICIENT) : chipColor;
  const hoverAccent = useDarken ? darken(accent, 0.08) : accent;
  const pressedAccent = useDarken ? darken(accent, 0.14) : accent;

  /** Text on filled background: optional override, else polished `readableColor` (WCAG luminance threshold 0.179). */
  const filledText = (bg: string) => filledLabelColor ?? readableColor(bg);

  const baseSx = {
    borderColor: accent,
    color: variant === 'outlined' ? accent : filledText(accent),
    ...(variant === 'filled' ? { bgcolor: accent } : {}),
    '&.MuiChip-clickable:hover': {
      borderColor: hoverAccent,
      ...(variant === 'filled'
        ? { bgcolor: hoverAccent, color: filledText(hoverAccent) }
        : {
            bgcolor: alpha(accent, 0.12),
          }),
    },
    '&.MuiChip-clickable:active': {
      borderColor: pressedAccent,
      ...(variant === 'filled'
        ? { bgcolor: pressedAccent, color: filledText(pressedAccent) }
        : {
            bgcolor: alpha(accent, 0.2),
          }),
    },
    '&.Mui-focusVisible': {
      borderColor: hoverAccent,
      ...(variant === 'filled'
        ? { bgcolor: hoverAccent, color: filledText(hoverAccent) }
        : {
            bgcolor: alpha(accent, 0.12),
          }),
    },
  };

  return (
    <Chip
      color="default"
      variant={variant}
      sx={sx == null ? baseSx : [baseSx, ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    />
  );
}
